import { Box } from '@highlight-cp/core-ui';

import classes from './pulse-logo.module.css';
import { PulseMark } from './pulse-mark';
import { PulseSquareMark } from './pulse-square-mark';

export interface PulseLogoProps {
  /** Drift the square tile's glows (`logo_square_animated.svg`). Ignored by `standalone`. */
  animated?: boolean;
  /**
   * Hide the mark from assistive tech. Defaults to true because the mark almost
   * always sits beside a visible "Pulse" label; pass `false` for a standalone mark.
   */
  decorative?: boolean;
  /** Edge length of the mark in px (default 16). */
  size?: number;
  /**
   * `standalone` is the bare halftone mark (`logo_standalone_color.svg`), for beside a label.
   * `square` is the app tile (`logo_square.svg`), for a hero spot.
   */
  variant?: 'standalone' | 'square';
}

/**
 * ### PulseLogo
 *
 * @description
 * The Pulse halftone mark, each dot in its Highlight brand color, or the square app tile.
 *
 * @example
 * <PulseLogo />
 * <PulseLogo decorative={false} size={44} variant="square" />
 * <PulseLogo animated size={64} variant="square" />
 *
 * @param props - {@link PulseLogoProps}
 * @returns React component
 */
export const PulseLogo = ({
  animated,
  decorative = true,
  size,
  variant = 'standalone',
}: PulseLogoProps) => {
  const accessibleName = decorative
    ? { 'aria-hidden': true as const }
    : { role: 'img' as const, 'aria-label': 'Pulse' };

  return (
    <Box className={classes.frame} h={size} w={size} {...accessibleName}>
      {variant === 'square' ? (
        <PulseSquareMark animated={animated} size={size} />
      ) : (
        <PulseMark size={size} />
      )}
    </Box>
  );
};
